import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Blockies from 'react-blockies'
import config from '../config.json'

import ethIcon from '../assets/eth.svg'
import OrdersPage from './OrdersPage'
import BalancePage from './BalancePage'
import BuySellModal from './BuySellModal'
import GoldVault from './GoldVault'
import SwapPage from './SwapPage'

const ProfileDropdown = ({ isOpen, onClose }) => {
  const dispatch = useDispatch()
  const account = useSelector(state => state.provider.account)
  const chainId = useSelector(state => state.provider.chainId)
  const balance = useSelector(state => state.provider.balance)
  const [activeSection, setActiveSection] = useState(null)
  const [networkOpen, setNetworkOpen] = useState(false)

  const networks = [
    { id: '0xaa36a7', name: 'Sepolia', icon: ethIcon },
    { id: '0x1', name: 'Ethereum', icon: ethIcon },
  ]

  const currentHex = chainId ? `0x${chainId.toString(16)}` : '0'
  const currentNetwork = networks.find(n => n.id === currentHex)

  const switchNetwork = async (id) => {
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: id }]
    })
    setNetworkOpen(false)
  }

  if (!isOpen || !account) return null

  return (
    <>
      <div className="profile-dropdown__backdrop" onClick={() => { setActiveSection(null); onClose() }} />
      <div className="profile-dropdown">
        <div className="profile-dropdown__header">
          <Blockies
            seed={account}
            size={10}
            scale={4}
            color="#FFD700"
            bgColor="#F5F0E8"
            spotColor="#B8960C"
            className="identicon"
          />
          <div className="profile-dropdown__account">
            <a
              href={config[chainId] ? `${config[chainId].explorerURL}/address/${account}` : '#'}
              target='_blank'
              rel='noreferrer'
              className="profile-dropdown__address"
            >
              {account.slice(0, 7) + '...' + account.slice(37, 42)}
            </a>
            <span className="profile-dropdown__balance">
              {balance ? Number(balance).toFixed(4) : '0'} ETH
            </span>
          </div>
          {chainId && (
            <div className="network-picker">
              <button className="network-picker__btn" onClick={() => setNetworkOpen(!networkOpen)}>
                {currentNetwork && <img src={currentNetwork.icon} alt={currentNetwork.name} />}
                <span>{currentNetwork ? currentNetwork.name : 'Network'}</span>
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                  <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {networkOpen && (
                <div className="network-picker__list">
                  {networks.map(n => (
                    <button
                      key={n.id}
                      className={`network-picker__option ${n.disabled ? 'network-picker__option--disabled' : ''} ${n.id === currentHex ? 'network-picker__option--active' : ''}`}
                      onClick={() => !n.disabled && switchNetwork(n.id)}
                      disabled={n.disabled}
                    >
                      <img src={n.icon} alt={n.name} />
                      <span>{n.name}</span>
                      {n.soonLabel && <small>{n.soonLabel}</small>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button className="profile-dropdown__disconnect-btn" onClick={() => { dispatch({ type: 'ACCOUNT_LOADED', account: null }); dispatch({ type: 'ETHER_BALANCE_LOADED', balance: null }); setActiveSection(null); onClose() }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17l5-5-5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Disconnect
          </button>
        </div>

        <div className="profile-dropdown__menu">
          <button className={`profile-dropdown__item ${activeSection === 'buysell' ? 'profile-dropdown__item--active' : ''}`} onClick={() => setActiveSection(activeSection === 'buysell' ? null : 'buysell')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Buy / Sell
          </button>
          <button className={`profile-dropdown__item ${activeSection === 'vault' ? 'profile-dropdown__item--active' : ''}`} onClick={() => setActiveSection(activeSection === 'vault' ? null : 'vault')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 4H3a1 1 0 00-1 1v14a1 1 0 001 1h18a1 1 0 001-1V5a1 1 0 00-1-1z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 12a2 2 0 11-4 0 2 2 0 014 0z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M2 8h20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Gold Vault
          </button>
          <button className={`profile-dropdown__item ${activeSection === 'orders' ? 'profile-dropdown__item--active' : ''}`} onClick={() => setActiveSection(activeSection === 'orders' ? null : 'orders')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <rect x="9" y="3" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Orders
          </button>
          <button className={`profile-dropdown__item ${activeSection === 'balance' ? 'profile-dropdown__item--active' : ''}`} onClick={() => setActiveSection(activeSection === 'balance' ? null : 'balance')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="6" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M2 10h20" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M6 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Balance
          </button>
          <button className={`profile-dropdown__item ${activeSection === 'swap' ? 'profile-dropdown__item--active' : ''}`} onClick={() => setActiveSection(activeSection === 'swap' ? null : 'swap')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 16V4M7 4L3 8M7 4L11 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M17 8V20M17 20L21 16M17 20L13 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Swap
          </button>
        </div>

        {activeSection && (
          <div key={activeSection} className="profile-dropdown__content">
            {activeSection === 'buysell' && (
              <BuySellModal isOpen={true} onClose={() => setActiveSection(null)} embedded />
            )}
            {activeSection === 'vault' && (
              <GoldVault embedded />
            )}
            {activeSection === 'orders' && (
              <OrdersPage onClose={() => setActiveSection(null)} embedded />
            )}
            {activeSection === 'balance' && (
              <BalancePage onClose={() => setActiveSection(null)} embedded />
            )}
            {activeSection === 'swap' && (
              <SwapPage embedded />
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default ProfileDropdown
